
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { type CaixaLotteryType, type ClosureType, CAIXA_LOTTERIES } from "@/utils/caixa";
import { type SubscriptionPlan } from "@/utils/predictionUtils";
import { generateClosureGames } from "@/utils/caixa/closureGenerator";
import FixedNumbersSelector from "./FixedNumbersSelector";

interface ClosureSelectorProps {
  lotteryType: CaixaLotteryType;
  onClosureSelect: (closure: ClosureType, games: Array<{ numbers: string[] }>, fixedNumbers?: number[]) => void;
  selectedClosure: string;
  onClosureChange: (closure: string) => void;
  userPlan: SubscriptionPlan;
}

const ClosureSelector: React.FC<ClosureSelectorProps> = ({ 
  lotteryType, 
  onClosureSelect,
  selectedClosure,
  onClosureChange,
  userPlan
}) => {
  const lottery = CAIXA_LOTTERIES[lotteryType];
  const [selectedClosureObj, setSelectedClosureObj] = useState<ClosureType | null>(null);
  const [fixedNumbers, setFixedNumbers] = useState<number[]>([]);
  const [showFixedSelector, setShowFixedSelector] = useState(false);

  const handleClosureClick = (closure: ClosureType) => {
    if (closure.hasFixedNumbers) {
      setSelectedClosureObj(closure);
      setShowFixedSelector(true);
      setFixedNumbers([]);
    } else {
      // Gerar jogos imediatamente para fechamentos sem números fixos
      const games = generateClosureGames(lotteryType, closure);
      onClosureSelect(closure, games);
      onClosureChange(closure.id);
    }
  };

  const handleConfirmWithFixed = () => {
    if (selectedClosureObj && fixedNumbers.length > 0) {
      const games = generateClosureGames(lotteryType, selectedClosureObj, fixedNumbers);
      onClosureSelect(selectedClosureObj, games, fixedNumbers);
      onClosureChange(selectedClosureObj.id);
      setShowFixedSelector(false);
      setSelectedClosureObj(null);
      setFixedNumbers([]);
    }
  };

  const handleCancelFixed = () => {
    setShowFixedSelector(false);
    setSelectedClosureObj(null);
    setFixedNumbers([]);
  };

  const getMaxFixedNumbers = (closure: ClosureType) => {
    if (lotteryType === "mega-sena" || lotteryType === "dupla-sena") return 3;
    if (lotteryType === "quina") return 3;
    if (lotteryType === "lotofacil") return 5;
    if (lotteryType === "dia-de-sorte") return 3;
    return 3;
  };

  if (showFixedSelector && selectedClosureObj) {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <h3 className="text-xl font-bold text-gold-700 dark:text-gold-300 mb-2">
            🎯 {selectedClosureObj.name} - {lottery.name}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {selectedClosureObj.description}
          </p>
        </div>

        <FixedNumbersSelector
          lotteryType={lotteryType}
          maxFixedNumbers={getMaxFixedNumbers(selectedClosureObj)}
          onFixedNumbersChange={setFixedNumbers}
          selectedNumbers={fixedNumbers}
        />

        <div className="flex justify-center gap-3">
          <Button
            onClick={handleConfirmWithFixed}
            disabled={fixedNumbers.length === 0}
            className="bg-gradient-to-r from-gold-500 to-yellow-500 hover:from-gold-600 hover:to-yellow-600 text-white"
          >
            ✓ Confirmar Fechamento com Fixas
          </Button>
          <Button
            onClick={handleCancelFixed}
            variant="outline"
          >
            ← Voltar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-xl font-bold text-gold-700 dark:text-gold-300 mb-2">
          🎯 Fechamentos e Desdobramentos - {lottery.name}
        </h3>
        <p className="text-sm text-muted-foreground">
          Escolha uma estratégia otimizada para aumentar suas chances
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {lottery.closures.map((closure) => (
          <Card 
            key={closure.id} 
            className="hover:shadow-lg transition-all duration-300 hover:scale-105 border-gold-200 dark:border-gold-700"
          >
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <h4 className="font-bold text-lg text-gold-700 dark:text-gold-300">
                    {closure.name}
                  </h4>
                  <div className="flex gap-1">
                    {closure.isEconomic && (
                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                        💰 Econômico
                      </Badge>
                    )}
                    {closure.hasFixedNumbers && (
                      <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                        📌 Com Fixas
                      </Badge>
                    )}
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  {closure.description}
                </p>
                
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-muted/30 p-2 rounded">
                    <span className="font-medium">Dezenas:</span> {closure.totalNumbers}
                  </div>
                  <div className="bg-muted/30 p-2 rounded">
                    <span className="font-medium">Jogos:</span> {closure.gamesGenerated}
                  </div>
                </div>
                
                <div className="bg-gold-50 dark:bg-gold-950/20 p-2 rounded border border-gold-200 dark:border-gold-700">
                  <span className="text-xs font-medium text-gold-700 dark:text-gold-300">
                    🎯 Garantia: {closure.guarantee}
                  </span>
                </div>
                
                <Button 
                  onClick={() => handleClosureClick(closure)}
                  className="w-full bg-gradient-to-r from-gold-500 to-yellow-500 hover:from-gold-600 hover:to-yellow-600 text-white"
                >
                  {closure.hasFixedNumbers ? "Selecionar Dezenas Fixas" : "Usar Este Desdobramento"}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ClosureSelector;
